import type { ReactNode } from 'react';
import SmartLink from '@/components/SmartLink';

interface CtaButtonProps {
  /** Internal route (router Link) or in-page hash (plain anchor). */
  to: string;
  variant?: 'solid' | 'ghost';
  ariaLabel?: string;
  children: ReactNode;
}

// No `focus-visible:outline-none` — it would beat the global `a:focus-visible`
// outline in index.css on specificity and leave the button with no focus ring.
// `min-h-[44px]` in px, not rem padding: at a 14px root font `py-2.5` yields only ~36px,
// below CLAUDE.md's 44px tap-target floor.
const base =
  'inline-flex min-h-[44px] items-center rounded-md px-5 py-2.5 font-mono text-sm font-semibold uppercase tracking-[0.1em] transition duration-200';

const variants: Record<'solid' | 'ghost', string> = {
  solid: 'bg-primary-dark text-ink-900 hover:-translate-y-0.5 dark:bg-primary',
  ghost:
    'border border-[color:var(--hairline)] text-[color:var(--fg)] hover:border-primary-dark hover:text-primary-ink dark:hover:border-primary dark:hover:text-primary',
};

/** Shared call-to-action button in the mono "Signal & Ledger" style. */
const CtaButton = ({ to, variant = 'solid', ariaLabel, children }: CtaButtonProps): JSX.Element => (
  <SmartLink to={to} ariaLabel={ariaLabel} className={`${base} ${variants[variant]}`}>
    {children}
  </SmartLink>
);

export default CtaButton;
